import React, { useState, useEffect, useCallback } from 'react';
import { I18n } from 'react-redux-i18n';
import { Form, Button } from 'react-bootstrap';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import CCModal2025 from '../../../../../../components/ccModal2025';
import {
  getPayoutFleetMethods,
  withdrawalPreviewGsn,
  withdrawalRequestGsn,
} from './withdrawalBillingApi';
import banktransper from '../../../../../../assets/images/icons/banktransper.svg';
import paypal from '../../../../../../assets/images/icons/paypal.png';
import wise from '../../../../../../assets/images/icons/wise.png';
import {
  getPayoutMethodKind,
  getPayoutMethodDisplayName,
  PAYOUT_KIND,
} from '../netWorkPaymentMethod/utils/payoutMethodUtils';
import './WithdrawalBilling.scss';
import { formatCurrencyHasSymbol } from '../../../../../../utils/commonFunctions';
import { BiSolidZap } from 'react-icons/bi';
import { FiAlertCircle } from 'react-icons/fi';

const STEP_REQUEST = 1;
const STEP_REVIEW = 2;
const TRANSFER_TYPE_NORMAL = 0;
const TRANSFER_TYPE_INSTANT = 1;
const MIN_TRANSFER_AMOUNT = 50;

function maskConnectToken(gateway, connectToken) {
  if (!connectToken) return '—';
  const s = String(connectToken).trim();
  if (gateway === 'PayPal' && s.includes('@')) return s;
  if (gateway === 'Wise' && s.startsWith('@')) return s;
  if (/^\d+$/.test(s) && s.length >= 4) return `**** ${s.slice(-4)}`;
  if (s.length > 8) return `${s.slice(0, 4)}****${s.slice(-4)}`;
  return '**** ' + s.slice(-4);
}

function getPayToOptionLabel(m) {
  const label = getPayoutMethodDisplayName(m, { short: true });
  const masked = maskConnectToken(m.gateway, m.connectToken);
  return `${label} - ${masked}`;
}

function formatMoney(amount, currencyISO = 'USD') {
  return formatCurrencyHasSymbol(amount, currencyISO);
}

const WithdrawalBilling = ({
  isInvoiceBillingMode = false,
  isShow,
  onClose,
  availableBalance = 0,
  creditBalance = 0,
  currencyISO = 'USD',
  auth,
  notification,
  loadingActions,
  onLoadBillingUsage,
}) => {
  const fleetId = auth?.selectedFleet?.fleetId ?? '';

  const [step, setStep] = useState(STEP_REQUEST);
  const [methods, setMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [amount, setAmount] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [transferType, setTransferType] = useState(TRANSFER_TYPE_NORMAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const balanceNum = Number(availableBalance) || 0;
  const amountNum = parseFloat(String(amount).replace(/[^0-9.]/g, '')) || 0;
  const canReview =
    selectedMethod &&
    amountNum >= MIN_TRANSFER_AMOUNT &&
    amountNum <= balanceNum;
  const canReviewInvoiceLimit = availableBalance + creditBalance > 0;
  const isPayPal = selectedMethod?.gateway === 'PayPal';
  const selectedMethodKind = getPayoutMethodKind(selectedMethod);
  const isBankTransfer = selectedMethodKind === PAYOUT_KIND.BANK_TRANSFER;
  const canChooseInstant = Boolean(selectedMethod && isBankTransfer);

  const fetchMethods = useCallback(async () => {
    if (!fleetId) return;
    setLoadingMethods(true);
    try {
      const list = await getPayoutFleetMethods({ fleetId });
      setMethods(list);
      if (list.length > 0) setSelectedMethod(list[0]);
    } catch (err) {
      notification?.(
        'error',
        err?.message || I18n.t('messages.commonMessages.error'),
      );
      setMethods([]);
    } finally {
      setLoadingMethods(false);
    }
  }, [fleetId, notification]);

  useEffect(() => {
    if (isShow && fleetId) {
      setStep(STEP_REQUEST);
      setAmount('');
      setPreview(null);
      setSelectedMethod(null);
      setTransferType(TRANSFER_TYPE_NORMAL);
      setError(null);
      fetchMethods();
    }
  }, [isShow, fleetId]);

  const handleReview = async () => {
    if (!selectedMethod || !canReview) return;
    // setError(null);
    setLoadingPreview(true);
    loadingActions.showLoadingSpiner();
    try {
      const data = await withdrawalPreviewGsn({
        fleetId,
        token: selectedMethod.connectToken,
        amount: amountNum,
        transferType: TRANSFER_TYPE_NORMAL,
      });
      if (data.res?.returnCode === 200) {
        setPreview(data.res?.response);
        setTransferType(TRANSFER_TYPE_NORMAL);
        setStep(STEP_REVIEW);
      } else if (data.res?.returnCode === 451) {
        notification?.(
          'error',
          isInvoiceBillingMode
            ? 'Insufficient Credit Limit'
            : 'Insufficient Wallet Balance',
        );
      } else {
        notification?.(
          'error',
          data.res?.message || I18n.t('messages.commonMessages.error'),
        );
        // setError(data.res?.message || I18n.t('messages.commonMessages.error'));
      }
    } catch (err) {
      // setError(err?.message || I18n.t('messages.commonMessages.error'));
      notification?.(
        'error',
        err?.message || I18n.t('messages.commonMessages.error'),
      );
    } finally {
      setLoadingPreview(false);
      loadingActions.hideLoadingSpiner();
    }
  };

  const handleTransferTypeChange = async (type) => {
    if (type === TRANSFER_TYPE_INSTANT && !canChooseInstant) return;
    setTransferType(type);
    if (!selectedMethod) return;
    setLoadingPreview(true);
    setError(null);
    try {
      loadingActions.showLoadingSpiner();
      const data = await withdrawalPreviewGsn({
        fleetId,
        token: selectedMethod.connectToken,
        amount: amountNum,
        transferType: Number(type),
      });
      if (data.res?.returnCode === 200) {
        setPreview(data.res?.response);
      } else {
        notification?.(
          'error',
          data.res?.message || I18n.t('messages.commonMessages.error'),
        );
      }
    } catch (err) {
      notification?.(
        'error',
        err?.message || I18n.t('messages.commonMessages.error'),
      );
    } finally {
      setLoadingPreview(false);
      loadingActions.hideLoadingSpiner();
    }
  };

  const handleConfirm = async () => {
    if (!selectedMethod || !preview) return;
    setError(null);
    setSubmitting(true);
    try {
      await withdrawalRequestGsn({
        fleetId,
        token: selectedMethod.connectToken,
        amount: amountNum,
        transferType,
      });
      notification?.(
        'success',
        I18n.t('billingUsage.withdrawalGsn.requestSuccess'),
      );
      onClose();
      onLoadBillingUsage();
    } catch (err) {
      setError(err?.message || I18n.t('messages.commonMessages.error'));
      notification?.(
        'error',
        err?.message || I18n.t('messages.commonMessages.error'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep(STEP_REQUEST);
    setError(null);
  };

  const handleClose = () => {
    setStep(STEP_REQUEST);
    setAmount('');
    setPreview(null);
    setSelectedMethod(null);
    setError(null);
    onClose();
  };

  const renderTransferToIcon = (method) => {
    const kind = getPayoutMethodKind(method);
    if (kind === PAYOUT_KIND.PAYPAL) {
      return <img src={paypal} alt="" className="withdrawal-method-icon" />;
    }
    if (kind === PAYOUT_KIND.WISE) {
      return <img src={wise} alt="" className="withdrawal-method-icon" />;
    }
    return (
      <img src={banktransper} alt="" className="withdrawal-method-icon" />
    );
  };

  const renderPayoutBalanceInfo = () => {
    return (
      <div className="available-balance-value">
        <span className="available-balance-value-label">
          {I18n.t('billingUsage.withdrawalGsn.payoutBalance').replace(
            '{currency}',
            currencyISO,
          )}
        </span>
        <span className="available-balance-value-amount">
          {formatMoney(balanceNum, currencyISO)}
        </span>
      </div>
    );
  };

  const renderTransferToInfo = () => {
    return (
      <Form.Group className="mb-3">
        <Form.Label>{I18n.t('billingUsage.withdrawalGsn.payTo')}</Form.Label>
        <Form.Control
          as="select"
          className="form-custom"
          value={
            selectedMethod
              ? Math.max(
                  0,
                  methods.findIndex(
                    (m) =>
                      m.connectToken === selectedMethod.connectToken &&
                      m.gateway === selectedMethod.gateway,
                  ),
                )
              : ''
          }
          onChange={(e) => {
            const idx = parseInt(e.target.value, 10);
            if (idx >= 0 && methods[idx]) setSelectedMethod(methods[idx]);
          }}
          disabled={loadingMethods}
        >
          {methods.length === 0 && (
            <option value="">
              {I18n.t('billingUsage.withdrawalGsn.noPayoutMethods')}
            </option>
          )}
          {methods.map((m, idx) => (
            <option key={`${m.gateway}-${m.connectToken}-${idx}`} value={idx}>
              {getPayToOptionLabel(m)}
            </option>
          ))}
        </Form.Control>
      </Form.Group>
    );
  };

  const renderInputAmountToWithdraw = () => {
    return (
      <Form.Group className="mb-3">
        <Form.Label>
          {I18n.t('billingUsage.withdrawalGsn.amountToWithdraw').replace(
            '{currency}',
            currencyISO,
          )}
        </Form.Label>
        <Form.Control
          type="text"
          placeholder={`0.00`}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="form-custom"
        />
        <Form.Text className="text-muted">
          {I18n.t('billingUsage.withdrawalGsn.minimumTransferAmount')
            .replace('{amount}', MIN_TRANSFER_AMOUNT)
            .replace('{currency}', currencyISO)}
        </Form.Text>
      </Form.Group>
    );
  };

  const renderInvoicePayoutBalanceInfo = () => {
    return (
      <>
        <div
          className="withdrawal-balance-info"
          style={{ marginBottom: '20px' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#9CA3AF' }}>
              {I18n.t('billingUsage.withdrawalGsn.payoutBalanceLabel')}
            </span>
            <span style={{ fontSize: '16px', fontWeight: '600' }}>
              {formatMoney(availableBalance, currencyISO)}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #3f4450',
              paddingBottom: '10px',
            }}
          >
            <span style={{ color: '#9CA3AF' }}>
              {I18n.t('billingUsage.withdrawalGsn.amountOwedToPlatform')}
            </span>
            <span
              style={{
                color: creditBalance > 0 ? '#04BE76' : '#FF6467',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              {formatMoney(creditBalance, currencyISO)}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#9CA3AF', fontWeight: '600' }}>
              {I18n.t('billingUsage.withdrawalGsn.availableToWithdraw')}
            </span>
            <span
              style={{
                color: availableBalance + creditBalance > 0 ? '#04BE76' : '#FF6467',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              {formatMoney(availableBalance + creditBalance, currencyISO)}
            </span>
          </div>
        </div>
      </>
    );
  };

  const renderAutoInfo = () => {
    return (
      <>
        {renderPayoutBalanceInfo()}
        {renderTransferToInfo()}
        {renderInputAmountToWithdraw()}
      </>
    );
  };

  const renderInvoiceInfo = () => {
    return (
      <>
        {renderPayoutBalanceInfo()}
        {renderInvoicePayoutBalanceInfo()}
        {canReviewInvoiceLimit ? (
          <>
            {renderTransferToInfo()}
            {renderInputAmountToWithdraw()}
          </>
        ) : (
          <div className="warning-current-balance">
            <span>
              <FiAlertCircle size={20} color="#F87171" />
            </span>
            <span>{I18n.t('billingUsage.warningCurrentBalance')}</span>
          </div>
        )}
      </>
    );
  };

  return (
    <CCModal2025
      isShow={true}
      onClose={handleClose}
      className={'withdrawal-review-modal'}
      title={
        <div className="withdrawal-review-header">
          {step === STEP_REVIEW && (
            <button
              type="button"
              className="btn-back"
              onClick={handleBack}
              aria-label="Back"
            >
              <HiOutlineArrowLeft size={24} />
            </button>
          )}
          <span className="review-title">
            {step === STEP_REQUEST
              ? I18n.t('billingUsage.withdrawalGsn.requestWithdrawal')
              : I18n.t('billingUsage.withdrawalGsn.reviewWithdrawal')}
          </span>
        </div>
      }
      hideSubmitButton
      hideFooter
      onCloseFooter={handleClose}
    >
      {step === STEP_REQUEST && (
        <div className="withdrawal-billing-step1">
          {isInvoiceBillingMode ? renderInvoiceInfo() : renderAutoInfo()}
          {error && <div className="alert alert-danger mb-3">{error}</div>}

          <div className="withdrawal-step1-actions">
            <Button
              className="btn-header text-add-header"
              onClick={handleClose}
            >
              {I18n.t('driver.Cancel')}
            </Button>
            {(!isInvoiceBillingMode || canReviewInvoiceLimit) && (
              <Button
                className="btn-save"
                onClick={handleReview}
                disabled={!canReview || loadingPreview}
              >
                {loadingPreview
                  ? I18n.t('networkSetting.Loading')
                  : I18n.t('billingUsage.withdrawalGsn.review')}
              </Button>
            )}
          </div>
        </div>
      )}

      {step === STEP_REVIEW && (
        <div className="withdrawal-billing-step2">
          {selectedMethod && (
            <div className="withdrawal-transfer-to mb-4">
              <div className="transfer-to-icon">
                {renderTransferToIcon(selectedMethod)}
              </div>
              <div>
                <div className="transfer-to-type">Transfer to</div>
                <div className="transfer-to-masked">
                  {maskConnectToken(
                    selectedMethod.gateway,
                    selectedMethod.connectToken,
                  )}
                </div>
                <div className="transfer-to-type">
                  {getPayoutMethodDisplayName(selectedMethod, { short: true })}
                </div>
              </div>
            </div>
          )}

          <div className="withdrawal-payout-speed mb-4">
            <div className="speed-section-label">
              {I18n.t('billingUsage.withdrawalGsn.payoutSpeed')}
            </div>
            <div
              className={`withdrawal-speed-option ${transferType === TRANSFER_TYPE_NORMAL ? 'selected' : ''}`}
              onClick={() => handleTransferTypeChange(TRANSFER_TYPE_NORMAL)}
            >
              <div className="speed-radio" />
              <div className="speed-content">
                <div className="speed-fee-days">
                  <span className="speed-name">
                    {I18n.t('billingUsage.withdrawalGsn.normalPayout')}
                  </span>
                  {canChooseInstant && (
                    <span className="speed-recommended">
                      {I18n.t('billingUsage.withdrawalGsn.recommended')}
                    </span>
                  )}
                </div>
                <div className="speed-fee-days">
                  <span className="speed-fee">
                    {I18n.t(
                      'billingUsage.withdrawalGsn.transferFeePercent',
                    ).replace('{percent}', 1)}
                  </span>
                  <span className="">
                    {isPayPal ? '0-1 business days' : '5-7 business days'}
                  </span>
                </div>
              </div>
            </div>
            {canChooseInstant && (
              <div
                className={`withdrawal-speed-option ${transferType === TRANSFER_TYPE_INSTANT ? 'selected' : ''} ${!canChooseInstant ? 'disabled' : ''}`}
                onClick={() =>
                  canChooseInstant &&
                  handleTransferTypeChange(TRANSFER_TYPE_INSTANT)
                }
              >
                <div className="speed-radio" />
                <div className="speed-content">
                  <div className="speed-fee-days">
                    <span className="speed-name">
                      <BiSolidZap
                        size={18}
                        color="#EAB308"
                        style={{ marginRight: '8px' }}
                      />
                      {I18n.t('billingUsage.withdrawalGsn.instantPayout')}
                    </span>
                  </div>
                  <div className="speed-fee-days">
                    <span className="speed-fee">
                      {I18n.t(
                        'billingUsage.withdrawalGsn.transferFeePercent',
                      ).replace('{percent}', '2.9')}
                    </span>
                    <span className="">1-3 business days</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {preview && (
            <div className="withdrawal-summary mb-4">
              <div className="summary-row">
                <span className="summary-row-label">
                  {I18n.t('billingUsage.withdrawalGsn.withdrawalAmount')}
                </span>
                <span>{formatMoney(amountNum, currencyISO)}</span>
              </div>
              <div className="summary-row fee-row">
                <span className="summary-row-label">
                  {I18n.t('billingUsage.withdrawalGsn.transferFee')}
                </span>
                <span className="summary-row-value">
                  - {formatMoney(preview.transFee, currencyISO)}
                </span>
              </div>
              {isBankTransfer && (
                <div className="summary-row fee-row">
                  <span className="summary-row-label">Amount deducted</span>
                  <span>
                    {formatMoney(preview.receivedAmount, currencyISO)}
                  </span>
                </div>
              )}
              <div className="summary-row total-row">
                <span>
                  {!isBankTransfer
                    ? 'Total to receive'
                    : I18n.t('billingUsage.withdrawalGsn.youWillReceive')}
                </span>
                <span className="total-amount">
                  {formatMoney(preview.receivedAmount, currencyISO)}
                </span>
              </div>
            </div>
          )}
          {isBankTransfer && (
            <div className="withdrawal-disclaimer-container">
              <p className="withdrawal-disclaimer text-muted small">
                {I18n.t('billingUsage.withdrawalGsn.disclaimer')}
                <br />
                {I18n.t('billingUsage.withdrawalGsn.disclaimer1')}
                <br />
                {I18n.t('billingUsage.withdrawalGsn.disclaimer2')}
              </p>
            </div>
          )}

          {error && <div className="alert alert-danger mb-3">{error}</div>}

          <div className="withdrawal-step2-actions">
            <Button variant="secondary" onClick={handleBack}>
              {I18n.t('driver.Cancel')}
            </Button>
            <Button
              className="btn-save"
              onClick={handleConfirm}
              disabled={submitting || loadingPreview}
            >
              {submitting
                ? I18n.t('networkSetting.Loading')
                : I18n.t('billingUsage.withdrawalGsn.confirmWithdrawal')}
            </Button>
          </div>
        </div>
      )}
    </CCModal2025>
  );
};

export default WithdrawalBilling;
